import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { useSnackbar } from '@Providers/useSnackbar';
import { getEducationalInfo } from '@Features/classrooms/educationalSlice';
import { GridLayout } from '@Styled/utilities';
import EducationSection, { EducationSectionRow } from './EducationSection';
import EducationEntityDialog, {
	EducationDialogInitial,
} from './EducationEntityDialog';
import {
	deleteEducationEntity,
	getEducationAll,
} from './education.requester';
import {
	EducationAllData,
	EducationEntity,
	EDUCATION_ENTITY_LABELS,
} from './education.types';

type DialogState = {
	entity: EducationEntity;
	initial: EducationDialogInitial | null;
};

type DeleteState = {
	entity: EducationEntity;
	id: number;
	name: string;
};

const EducationalTables = () => {
	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const [data, setData] = useState<EducationAllData | null>(null);
	const [dialog, setDialog] = useState<DialogState | null>(null);
	const [pendingDelete, setPendingDelete] = useState<DeleteState | null>(null);

	const loadAll = useCallback(async () => {
		try {
			const {
				data: { data },
			} = await getEducationAll();
			setData(data);
		} catch (error) {
			displaySnackbar('error', 'Unable to load educational tables');
		}
	}, []);

	useEffect(() => {
		loadAll();
	}, []);

	const refreshAfterMutation = () => {
		loadAll();
		dispatch(getEducationalInfo());
	};

	const onSaved = () => {
		setDialog(null);
		refreshAfterMutation();
	};

	const onConfirmDelete = async (close: boolean, confirm?: boolean) => {
		const target = pendingDelete;
		setPendingDelete(null);
		if (!confirm || !target) return;
		try {
			await deleteEducationEntity(target.entity, target.id);
			displaySnackbar(
				'success',
				`${EDUCATION_ENTITY_LABELS[target.entity]} "${target.name}" deleted successfully`
			);
			refreshAfterMutation();
		} catch (error: any) {
			const message =
				error?.response?.data?.message ??
				`Unable to delete ${EDUCATION_ENTITY_LABELS[target.entity].toLowerCase()}`;
			displaySnackbar('error', message);
		}
	};

	const typeRows = useMemo<EducationSectionRow[]>(
		() =>
			(data?.types ?? []).map((type) => ({
				id: type.id,
				name: type.name,
				chips: [
					...(type.educationLanguages ?? []).map((_) => _.name),
					...(type.educationYears ?? []).map((_) => _.name),
				],
			})),
		[data]
	);

	const yearRows = useMemo<EducationSectionRow[]>(
		() =>
			(data?.years ?? []).map((year) => ({
				id: year.id,
				name: year.name,
				chips: (year.educationSections ?? []).map((_) => _.name),
			})),
		[data]
	);

	const languageRows = useMemo<EducationSectionRow[]>(
		() => (data?.languages ?? []).map(({ id, name }) => ({ id, name })),
		[data]
	);

	const sectionRows = useMemo<EducationSectionRow[]>(
		() => (data?.sections ?? []).map(({ id, name }) => ({ id, name })),
		[data]
	);

	const options = useMemo(
		() => ({
			languages: (data?.languages ?? []).map(({ id, name }) => ({ id, name })),
			years: (data?.years ?? []).map(({ id, name }) => ({ id, name })),
			sections: (data?.sections ?? []).map(({ id, name }) => ({ id, name })),
		}),
		[data]
	);

	const openEditType = (row: EducationSectionRow) => {
		const type = data?.types.find((_) => _.id === row.id);
		if (!type) return;
		setDialog({
			entity: 'type',
			initial: {
				id: type.id,
				name: type.name,
				language_ids: (type.educationLanguages ?? []).map((_) => _.id),
				year_ids: (type.educationYears ?? []).map((_) => _.id),
			},
		});
	};

	const openEditYear = (row: EducationSectionRow) => {
		const year = data?.years.find((_) => _.id === row.id);
		if (!year) return;
		setDialog({
			entity: 'year',
			initial: {
				id: year.id,
				name: year.name,
				section_ids: (year.educationSections ?? []).map((_) => _.id),
			},
		});
	};

	const openEditFlat = (entity: EducationEntity) => (row: EducationSectionRow) => {
		setDialog({ entity, initial: { id: row.id, name: row.name } });
	};

	const requestDelete = (entity: EducationEntity) => (row: EducationSectionRow) => {
		setPendingDelete({ entity, id: row.id, name: row.name });
	};

	return (
		<Container>
			<SectionsLayout>
				<EducationSection
					title="Education Types"
					chipsHeader="LINKED LANGUAGES & YEARS"
					rows={typeRows}
					onAdd={() => setDialog({ entity: 'type', initial: null })}
					onEdit={openEditType}
					onDelete={requestDelete('type')}
				/>
				<EducationSection
					title="Education Years"
					chipsHeader="LINKED SECTIONS"
					rows={yearRows}
					onAdd={() => setDialog({ entity: 'year', initial: null })}
					onEdit={openEditYear}
					onDelete={requestDelete('year')}
				/>
				<EducationSection
					title="Education Languages"
					rows={languageRows}
					onAdd={() => setDialog({ entity: 'language', initial: null })}
					onEdit={openEditFlat('language')}
					onDelete={requestDelete('language')}
				/>
				<EducationSection
					title="Education Sections"
					rows={sectionRows}
					onAdd={() => setDialog({ entity: 'section', initial: null })}
					onEdit={openEditFlat('section')}
					onDelete={requestDelete('section')}
				/>
			</SectionsLayout>

			<EducationEntityDialog
				open={!!dialog}
				entity={dialog?.entity ?? 'type'}
				initial={dialog?.initial}
				languageOptions={options.languages}
				yearOptions={options.years}
				sectionOptions={options.sections}
				onClose={() => setDialog(null)}
				onSaved={onSaved}
			/>

			<EdConfirmModal
				open={!!pendingDelete}
				handleClose={onConfirmDelete}
				text={
					pendingDelete
						? `Are you sure you want to delete ${EDUCATION_ENTITY_LABELS[
								pendingDelete.entity
						  ].toLowerCase()} "${pendingDelete.name}"?`
						: ''
				}
			/>
		</Container>
	);
};

export default EducationalTables;

const Container = styled.div`
	padding: 2rem;
`;

const SectionsLayout = styled(GridLayout)`
	grid-template-columns: 1fr 1fr;
	gap: 1.5rem;
	align-items: start;

	@media (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;
